import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi, AuthUser } from '../services/auth.api';
import { SESSION_CONFIG } from '../config/session';

// Use centralized configuration
const { INACTIVITY_TIMEOUT, TOKEN_EXPIRY_BUFFER } = SESSION_CONFIG;

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  tokenExpiresAt: number | null; // Timestamp when access token expires
  lastActivityAt: number | null; // Timestamp of last user activity

  // Actions
  setAuth: (user: AuthUser, accessToken: string, refreshToken: string) => void;
  setLoading: (loading: boolean) => void;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  clearAuth: () => void;
  updateActivity: () => void; // Update last activity timestamp
  checkSession: () => boolean; // Check if session is valid
}

// Decode JWT to get expiration time
function decodeTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
}

let inactivityTimer: NodeJS.Timeout | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      tokenExpiresAt: null,
      lastActivityAt: null,

      setAuth: (user, accessToken, refreshToken) => {
        const tokenExpiresAt = decodeTokenExpiry(accessToken);
        const lastActivityAt = Date.now();
        
        set({ 
          user, 
          accessToken, 
          refreshToken, 
          isAuthenticated: true,
          tokenExpiresAt,
          lastActivityAt,
        });

        // Start inactivity timer
        get().updateActivity();
      },

      updateActivity: () => {
        const now = Date.now();
        set({ lastActivityAt: now });

        // Clear existing timer
        if (inactivityTimer) {
          clearTimeout(inactivityTimer);
        }

        // Set new inactivity timer
        inactivityTimer = setTimeout(() => {
          console.log('[Auth] Session expired due to inactivity');
          get().logout();
        }, INACTIVITY_TIMEOUT);
      },

      checkSession: () => {
        const { accessToken, tokenExpiresAt, lastActivityAt, isAuthenticated } = get();
        
        if (!isAuthenticated || !accessToken) {
          return false;
        }

        const now = Date.now();

        // Check token expiration
        if (tokenExpiresAt && now >= tokenExpiresAt - TOKEN_EXPIRY_BUFFER) {
          console.log('[Auth] Token expired');
          get().clearAuth();
          return false;
        }

        // Check inactivity timeout
        if (lastActivityAt && now - lastActivityAt >= INACTIVITY_TIMEOUT) {
          console.log('[Auth] Session expired due to inactivity');
          get().clearAuth();
          return false;
        }

        return true;
      },

      setLoading: (isLoading) => set({ isLoading }),

      clearAuth: () => {
        // Clear inactivity timer
        if (inactivityTimer) {
          clearTimeout(inactivityTimer);
          inactivityTimer = null;
        }

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          tokenExpiresAt: null,
          lastActivityAt: null,
        });
      },

      logout: async () => {
        const { refreshToken } = get();
        if (refreshToken) {
          try {
            await authApi.logout(refreshToken);
          } catch {
            // Logout even if API fails
          }
        }
        get().clearAuth();
      },

      fetchUser: async () => {
        try {
          set({ isLoading: true });
          const user = await authApi.getMe();
          set({ user, isLoading: false });
        } catch {
          get().clearAuth();
          throw new Error('Session invalid');
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        tokenExpiresAt: state.tokenExpiresAt,
        lastActivityAt: state.lastActivityAt,
      }),
    }
  )
);
